import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Switch } from "../../../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Loader2, CheckCircle2, AlertCircle, Copy } from "lucide-react";
import { supabase } from "../../../../../integrations/supabase/client";
import { useToast } from "../../../../../hooks/use-toast";

interface Outlet {
  id?: string;
  outlet_name: string;
  location?: string;
  manager_name?: string;
  email?: string;
  phone?: string;
  google_maps_url?: string;
  instagram_username?: string;
  tiktok_username?: string;
  facebook_url?: string;
  tripadvisor_url?: string;
  epic_outlet_id?: string;
  scrape_enabled?: boolean;
  fetch_limit?: number;
  google_scraper_version?: "google_places" | "google_reviews_v2";
  review_source?: "apify" | "oxylabs" | "dataforseo";
  business_name?: string;
  branch?: string;
  verified_status?: boolean;
  data_source?: string;
  last_verified_at?: string;
  verification_payload?: any;
  country_code?: string;
  location_code?: number;
  place_id?: string;
  data_fetch_mode?: "keyword" | "place_id";
  webhook_secret?: string;
}

interface OutletModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (outlet: Outlet) => Promise<void>;
  outlet?: Outlet | null;
}

const OutletModal = ({ open, onClose, onSave, outlet }: OutletModalProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{
    valid: boolean | null;
    reason?: string;
    placeName?: string;
    normalizedUrl?: string;
  }>({ valid: null });
  
  const [verificationStatus, setVerificationStatus] = useState<{
    verified: boolean | null;
    reviewCount?: number;
    placeName?: string;
    taskId?: string;
  }>({ verified: null });
  
  const [verifying, setVerifying] = useState(false);
  const [tripAdvisorScraping, setTripAdvisorScraping] = useState(false);
  const [tripAdvisorStatus, setTripAdvisorStatus] = useState<{
    scraping: boolean;
    success?: boolean;
    message?: string;
  }>({ scraping: false });

  // Country to location code mapping
const COUNTRY_MAPPINGS = [
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', location_code: 2707, full_name: 'Singapore' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', location_code: 2688, full_name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', location_code: 2498, full_name: 'Indonesia' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', location_code: 2826, full_name: 'Philippines' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', location_code: 2768, full_name: 'Thailand' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', location_code: 2704, full_name: 'Vietnam' },
  { code: 'IN', name: 'India', flag: '🇮🇳', location_code: 2292, full_name: 'India' },
];
  
  const [formData, setFormData] = useState<Outlet>({
    outlet_name: "",
    location: "",
    google_maps_url: "",
    instagram_username: "",
    tiktok_username: "",
    facebook_url: "",
    tripadvisor_url: "",
    scrape_enabled: true,
    fetch_limit: undefined,
    google_scraper_version: "google_places",
    review_source: "apify",
    country_code: "SG",
    location_code: 2707,
    data_fetch_mode: "keyword",
    place_id: "",
  });

  const [platformToggles, setPlatformToggles] = useState({
    googleMaps: false,
    instagram: false,
    tiktok: false,
    facebook: false,
    tripadvisor: false,
    epicreviews: false,
  });
  
  const [epicScraping, setEpicScraping] = useState(false);

  useEffect(() => {
    if (outlet) {
      setFormData(outlet);
      setPlatformToggles({
        googleMaps: !!outlet.google_maps_url,
        instagram: !!outlet.instagram_username,
        tiktok: !!outlet.tiktok_username,
        facebook: !!outlet.facebook_url,
        tripadvisor: !!outlet.tripadvisor_url,
        epicreviews: !!outlet.epic_outlet_id,
      });
      // Reset validation when editing existing outlet
      setValidationStatus({ valid: null });
    } else {
      setFormData({
        outlet_name: "",
        location: "",
        google_maps_url: "",
        instagram_username: "",
        tiktok_username: "",
        facebook_url: "",
        tripadvisor_url: "",
        scrape_enabled: true,
        fetch_limit: undefined,
        google_scraper_version: "google_places",
        review_source: "apify",
        data_fetch_mode: "keyword",
        place_id: "",
      });
      setPlatformToggles({
        googleMaps: false,
        instagram: false,
        tiktok: false,
        facebook: false,
        tripadvisor: false,
        epicreviews: false,
      });
      setValidationStatus({ valid: null });
    }
  }, [outlet]);

  const validateGoogleMapsUrl = async () => {
    if (!formData.google_maps_url) {
      toast({
        title: "Error",
        description: "Please enter a Google Maps URL first",
        variant: "destructive",
      });
      return;
    }

    setValidating(true);
    setValidationStatus({ valid: null });

    try {
      const { data, error } = await supabase.functions.invoke('validate-google-maps-url', {
        body: { url: formData.google_maps_url },
      });

      if (error) throw error;

      if (data.valid) {
        setValidationStatus({
          valid: true,
          placeName: data.placeName,
          normalizedUrl: data.normalizedUrl,
        });
        // Update the form with normalized URL
        if (data.normalizedUrl && data.normalizedUrl !== formData.google_maps_url) {
          setFormData({ ...formData, google_maps_url: data.normalizedUrl });
          toast({
            title: "✅ Location Verified & Normalized",
            description: `Found: ${data.placeName}. URL was converted to canonical format.`,
          });
        } else {
          toast({
            title: "✅ Location Verified",
            description: `Found: ${data.placeName}`,
          });
        }
      } else {
        setValidationStatus({
          valid: false,
          reason: data.reason,
        });
        toast({
          title: "⚠️ Validation Failed",
          description: data.reason,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Validation error:", error);
      setValidationStatus({
        valid: false,
        reason: error.message || "Failed to validate URL",
      });
      toast({
        title: "Error",
        description: "Failed to validate URL. Please try again.",
        variant: "destructive",
      });
    } finally {
      setValidating(false);
    }
  };

  const extractPlaceIdFromUrl = (url: string): string | null => {
    const match = url.match(/Ch[A-Za-z0-9_-]+/);
    return match ? match[0] : null;
  };

  const extractTripAdvisorUrlPath = (url: string): string | null => {
    // Extract the Restaurant_Review... part from TripAdvisor URL
    const match = url.match(/Restaurant_Review[^/]+/);
    return match ? match[0] : null;
  };

  const startTripAdvisorScraping = async () => {
    if (!formData.tripadvisor_url || !formData.id) {
      toast({
        title: "Error",
        description: "Please save the outlet first with a TripAdvisor URL",
        variant: "destructive",
      });
      return;
    }

    const urlPath = extractTripAdvisorUrlPath(formData.tripadvisor_url);
    if (!urlPath) {
      toast({
        title: "Invalid TripAdvisor URL",
        description: "Please enter a valid TripAdvisor restaurant URL",
        variant: "destructive",
      });
      return;
    }

    setTripAdvisorScraping(true);
    setTripAdvisorStatus({ scraping: true, message: "Starting TripAdvisor scraping..." });

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create a scraping job first
      const { data: jobData, error: jobError } = await supabase
        .from('scraping_jobs')
        .insert({
          user_id: user.id,
          outlet_id: formData.id,
          platform: 'tripadvisor',
          source: 'DATAFORSEO',
          status: 'queued',
          message: 'TripAdvisor scraping started'
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('tripadvisor-fetch-reviews', {
        body: {
          url_path: urlPath,
          language_name: 'English',
          depth: formData.fetch_limit || 50,
          jobId: jobData.id,
          outlet_id: formData.id,
        },
      });

      if (error) throw error;

      if (data.success) {
        setTripAdvisorStatus({
          scraping: false,
          success: true,
          message: `Successfully fetched ${data.reviews_count} TripAdvisor reviews`,
        });
        toast({
          title: "✅ TripAdvisor Scraping Complete",
          description: `Fetched ${data.reviews_count} reviews. Check Sync Center for details.`,
        });
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error("TripAdvisor scraping error:", error);
      setTripAdvisorStatus({
        scraping: false,
        success: false,
        message: error.message || "Failed to scrape TripAdvisor reviews",
      });
      toast({
        title: "Error",
        description: error.message || "Failed to scrape TripAdvisor reviews",
        variant: "destructive",
      });
    } finally {
      setTripAdvisorScraping(false);
    }
  };

  const verifyBusiness = async () => {
    if (formData.data_fetch_mode === "place_id" && !formData.place_id) {
      toast({
        title: "Error",
        description: "Please enter a Place ID or Google Maps URL",
        variant: "destructive",
      });
      return;
    }
    
    if (formData.data_fetch_mode === "keyword" && !formData.outlet_name) {
      toast({
        title: "Error",
        description: "Please enter outlet name first",
        variant: "destructive",
      });
      return;
    }

    setVerifying(true);
    setVerificationStatus({ verified: null });
    
    const selectedCountry = COUNTRY_MAPPINGS.find(c => c.code === formData.country_code);

    try {
      const body: any = {
        country_name: selectedCountry?.full_name || 'Singapore',
        reviews_limit: formData.fetch_limit || 10
      };

      if (formData.data_fetch_mode === "place_id") {
        body.place_id = formData.place_id;
      } else {
        body.outlet_name = formData.outlet_name;
        body.branch = formData.location;
      }

      const { data, error } = await supabase.functions.invoke('dataforseo-fetch-reviews', {
        body,
      });

      if (error) throw error;

      if (data.ok && data.reviews_found > 0) {
        setVerificationStatus({
          verified: true,
          reviewCount: data.reviews_found,
          placeName: data.business_name,
          taskId: data.task_id,
        });
        
        // Update form with verification data
        setFormData({ 
          ...formData, 
          business_name: data.business_name,
          verified_status: true,
          data_source: 'dataforseo',
          verification_payload: data
        });

        toast({
          title: `✅ Business found in ${data.location}`,
          description: `"${data.business_name}" — ${data.reviews_found} reviews, ${data.average_rating.toFixed(1)}★ average`,
        });
      } else {
        setVerificationStatus({
          verified: false,
        });
        toast({
          title: `❌ No business found in ${selectedCountry?.name}`,
          description: formData.data_fetch_mode === "place_id" 
            ? "Try search mode instead or verify the Place ID." 
            : "Try refining the name or branch, or use Place ID mode.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      setVerificationStatus({
        verified: false,
      });
      toast({
        title: "Error",
        description: error.message || "Failed to verify business",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);

    try {
      const outletData = { ...formData };
      
      // Clear URLs for disabled platforms
      if (!platformToggles.googleMaps) outletData.google_maps_url = "";
      if (!platformToggles.instagram) outletData.instagram_username = "";
      if (!platformToggles.tiktok) outletData.tiktok_username = "";
      if (!platformToggles.facebook) outletData.facebook_url = "";
      if (!platformToggles.tripadvisor) outletData.tripadvisor_url = "";
      if (!platformToggles.epicreviews) outletData.epic_outlet_id = "";

      await onSave(outletData);
      onClose();
    } catch (error: any) {
      console.error("Error saving outlet:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save outlet. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{outlet ? "Edit Outlet" : "Add New Outlet"}</DialogTitle>
          <DialogDescription>
            Configure outlet information and platform connections
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} onKeyDown={(e) => {
          // Prevent Enter key from submitting form (only allow explicit button click)
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
            e.preventDefault();
          }
        }} className="space-y-6">
          {/* Outlet Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Outlet Information</h3>
            
            <div className="space-y-2">
              <Label htmlFor="outlet_name">Outlet Name *</Label>
              <Input
                id="outlet_name"
                placeholder="e.g., Gayatri Restaurant"
                value={formData.outlet_name}
                onChange={(e) => setFormData({ ...formData, outlet_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location / Branch</Label>
              <Input
                id="location"
                placeholder="e.g., Race Course, Downtown"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                💡 Adding a specific branch/location improves verification accuracy
              </p>
            </div>

            {/* Country Selector */}
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select
                value={formData.country_code || "SG"}
                onValueChange={(value) => {
                  const country = COUNTRY_MAPPINGS.find(c => c.code === value);
                  setFormData({ 
                    ...formData, 
                    country_code: value,
                    location_code: country?.location_code || 2707
                  });
                }}
              >
                <SelectTrigger id="country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_MAPPINGS.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.review_source === 'dataforseo' && (
              <div className="space-y-4">
                {/* Fetch Mode Toggle */}
                <div className="space-y-2">
                  <Label>DataForSEO Fetch Mode</Label>
                  <Select
                    value={formData.data_fetch_mode || "keyword"}
                    onValueChange={(value: "keyword" | "place_id") => {
                      setFormData({ ...formData, data_fetch_mode: value });
                      setVerificationStatus({ verified: null });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keyword">
                        🔍 Search by Business Name (Keyword)
                      </SelectItem>
                      <SelectItem value="place_id">
                        📍 Use Place ID (Recommended)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.data_fetch_mode === "place_id"
                      ? "💡 Place ID mode is faster and more accurate. Paste a Google Maps URL or ChI... ID."
                      : "💡 Search mode finds businesses by name. Switch to Place ID for better accuracy."}
                  </p>
                </div>

                {/* Place ID Input (only shown in place_id mode) */}
                {formData.data_fetch_mode === "place_id" && (
                  <div className="space-y-2">
                    <Label htmlFor="place_id">Google Maps Place ID or URL</Label>
                    <Input
                      id="place_id"
                      placeholder="ChIJ... or https://maps.google.com/..."
                      value={formData.place_id}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Try to extract Place ID if it's a URL
                        const extractedId = extractPlaceIdFromUrl(value);
                        setFormData({ 
                          ...formData, 
                          place_id: extractedId || value 
                        });
                        setVerificationStatus({ verified: null });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      Paste full URL or just the ChI... Place ID
                    </p>
                  </div>
                )}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={verifyBusiness}
                  disabled={
                    verifying || 
                    (formData.data_fetch_mode === "keyword" && !formData.outlet_name) ||
                    (formData.data_fetch_mode === "place_id" && !formData.place_id)
                  }
                  className="w-full"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying Business...
                    </>
                  ) : (
                    `🔍 Verify Business on Google ${formData.data_fetch_mode === "place_id" ? "(Place ID)" : "(Keyword)"}`
                  )}
                </Button>
                
                {verificationStatus.verified === true && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-md text-sm text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>
                      ✅ Verified – {verificationStatus.placeName} ({verificationStatus.reviewCount} reviews detected)
                    </span>
                  </div>
                )}
                
                {verificationStatus.verified === false && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md text-sm text-red-700 dark:text-red-300">
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-medium">❌ Business not found</p>
                      <p className="text-xs">
                        {formData.data_fetch_mode === "place_id"
                          ? "⚠️ No reviews found for this Place ID. Try search mode."
                          : "Please refine the name or add a specific branch."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Platform URLs */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Platform URLs / Usernames</h3>

            {/* Google Maps */}
            <div className="flex items-center justify-between">
              <Label htmlFor="google_toggle" className="flex items-center gap-2">
                🗺️ Google Maps
              </Label>
              <Switch
                id="google_toggle"
                checked={platformToggles.googleMaps}
                onCheckedChange={(checked) => {
                  setPlatformToggles({ ...platformToggles, googleMaps: checked });
                  setValidationStatus({ valid: null });
                }}
              />
            </div>
            {platformToggles.googleMaps && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="https://maps.google.com/... or https://maps.app.goo.gl/..."
                    value={formData.google_maps_url}
                    onChange={(e) => {
                      setFormData({ ...formData, google_maps_url: e.target.value });
                      setValidationStatus({ valid: null });
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validateGoogleMapsUrl}
                    disabled={validating || !formData.google_maps_url}
                  >
                    {validating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </>
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>
                
                {validationStatus.valid === true && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Location verified – Ready to scrape</span>
                    {validationStatus.placeName && (
                      <span className="text-muted-foreground">({validationStatus.placeName})</span>
                    )}
                  </div>
                )}
                
                {validationStatus.valid === false && (
                  <div className="flex items-start gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-medium">Invalid location</p>
                      <p className="text-xs">{validationStatus.reason}</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {formData.review_source === 'apify' 
                    ? '💡 Tip: Paste either the full URL (google.com/maps/place/...) or shortened link (maps.app.goo.gl/...) — both will be verified and normalized'
                    : '💡 Tip: DataForSEO accepts any Google Maps URL format. Verification is optional.'
                  }
                </p>
              </div>
            )}

            {/* Instagram */}
            <div className="flex items-center justify-between">
              <Label htmlFor="instagram_toggle" className="flex items-center gap-2">
                📷 Instagram
              </Label>
              <Switch
                id="instagram_toggle"
                checked={platformToggles.instagram}
                onCheckedChange={(checked) =>
                  setPlatformToggles({ ...platformToggles, instagram: checked })
                }
              />
            </div>
            {platformToggles.instagram && (
              <Input
                placeholder="@username"
                value={formData.instagram_username}
                onChange={(e) => setFormData({ ...formData, instagram_username: e.target.value })}
              />
            )}

            {/* TikTok */}
            <div className="flex items-center justify-between">
              <Label htmlFor="tiktok_toggle" className="flex items-center gap-2">
                🎵 TikTok
              </Label>
              <Switch
                id="tiktok_toggle"
                checked={platformToggles.tiktok}
                onCheckedChange={(checked) =>
                  setPlatformToggles({ ...platformToggles, tiktok: checked })
                }
              />
            </div>
            {platformToggles.tiktok && (
              <Input
                placeholder="@username"
                value={formData.tiktok_username}
                onChange={(e) => setFormData({ ...formData, tiktok_username: e.target.value })}
              />
            )}

            {/* Facebook */}
            <div className="flex items-center justify-between">
              <Label htmlFor="facebook_toggle" className="flex items-center gap-2">
                👍 Facebook
              </Label>
              <Switch
                id="facebook_toggle"
                checked={platformToggles.facebook}
                onCheckedChange={(checked) =>
                  setPlatformToggles({ ...platformToggles, facebook: checked })
                }
              />
            </div>
            {platformToggles.facebook && (
              <Input
                placeholder="https://facebook.com/..."
                value={formData.facebook_url}
                onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
              />
            )}

            {/* Tripadvisor */}
            <div className="flex items-center justify-between">
              <Label htmlFor="tripadvisor_toggle" className="flex items-center gap-2">
                ✈️ Tripadvisor
              </Label>
              <Switch
                id="tripadvisor_toggle"
                checked={platformToggles.tripadvisor}
                onCheckedChange={(checked) =>
                  setPlatformToggles({ ...platformToggles, tripadvisor: checked })
                }
              />
            </div>
            {platformToggles.tripadvisor && (
              <div className="space-y-2">
                <Input
                  placeholder="https://tripadvisor.com/Restaurant_Review-..."
                  value={formData.tripadvisor_url}
                  onChange={(e) => {
                    setFormData({ ...formData, tripadvisor_url: e.target.value });
                    setTripAdvisorStatus({ scraping: false });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  💡 Paste full TripAdvisor restaurant URL
                </p>
                
                {formData.id && formData.tripadvisor_url && (
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startTripAdvisorScraping}
                      disabled={tripAdvisorScraping}
                      className="w-full"
                    >
                      {tripAdvisorScraping ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Scraping TripAdvisor...
                        </>
                      ) : (
                        "🚀 Start TripAdvisor Scraping"
                      )}
                    </Button>
                    
                    {tripAdvisorStatus.success === true && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-md text-sm text-green-700 dark:text-green-300">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>{tripAdvisorStatus.message}</span>
                      </div>
                    )}
                    
                    {tripAdvisorStatus.success === false && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md text-sm text-red-700 dark:text-red-300">
                        <AlertCircle className="h-4 w-4 mt-0.5" />
                        <span>{tripAdvisorStatus.message}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {!formData.id && formData.tripadvisor_url && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Save the outlet first before starting TripAdvisor scraping
                  </p>
                )}
              </div>
            )}

            {/* EpicReviews */}
            <div className="flex items-center justify-between">
              <Label htmlFor="epicreviews_toggle" className="flex items-center gap-2">
                🟥 EpicReviews (Low-Star Feedback)
              </Label>
              <Switch
                id="epicreviews_toggle"
                checked={platformToggles.epicreviews}
                onCheckedChange={(checked) =>
                  setPlatformToggles({ ...platformToggles, epicreviews: checked })
                }
              />
            </div>
            {platformToggles.epicreviews && (
              <div className="space-y-2">
                <div className="space-y-2">
                  <Label htmlFor="epic_outlet_id">EpicPay Outlet ID *</Label>
                  <Input
                    id="epic_outlet_id"
                    placeholder="Enter EpicPay Outlet ID"
                    value={formData.epic_outlet_id}
                    onChange={(e) => setFormData({ ...formData, epic_outlet_id: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    💡 This is your outlet identifier in the EpicPay system
                  </p>
                </div>

                {formData.id && formData.epic_outlet_id && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      const bearerToken = localStorage.getItem('epic_bearer_token');
                      if (!bearerToken) {
                        toast({
                          title: "Error",
                          description: "Please set Bearer Token in Settings → EpicReviews API first",
                          variant: "destructive",
                        });
                        return;
                      }

                      setEpicScraping(true);
                      try {
                        const { data, error } = await supabase.functions.invoke('epicreviews-fetch-reviews', {
                          body: {
                            outletId: formData.id,
                            bearerToken,
                            limit: formData.fetch_limit || 10,
                            offset: 1
                          },
                        });

                        if (error) throw error;

                        if (data.status === 'ok') {
                          toast({
                            title: "✅ EpicReviews Fetch Started",
                            description: `Fetching ${data.resultCount || 0} low-star reviews. Check Sync Center.`,
                          });
                        } else {
                          throw new Error(data.error || 'Unknown error');
                        }
                      } catch (error: any) {
                        console.error("EpicReviews error:", error);
                        toast({
                          title: "Error",
                          description: error.message || "Failed to fetch EpicReviews",
                          variant: "destructive",
                        });
                      } finally {
                        setEpicScraping(false);
                      }
                    }}
                    disabled={epicScraping}
                    className="w-full"
                  >
                    {epicScraping ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Fetching EpicReviews...
                      </>
                    ) : (
                      "🚀 Fetch EpicReviews"
                    )}
                  </Button>
                )}

                {!formData.id && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Save the outlet first before fetching EpicReviews
                  </p>
                )}

                <p className="text-sm text-muted-foreground">
                  Only 1–3 star reviews will be fetched. View progress in Sync Center.
                </p>
              </div>
            )}

          {/* Webhook Configuration */}
          {formData.id && platformToggles.epicreviews && (
            <div className="space-y-4 pt-4 border-t border-dashed">
              <h3 className="text-sm font-semibold">🔐 Webhook Configuration (Real-Time Push)</h3>
              <p className="text-xs text-muted-foreground">
                Configure a webhook to receive reviews in real-time from EpicPay instead of manual fetching.
              </p>
              
              <div className="space-y-2">
                <Label>Webhook Secret</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={formData.webhook_secret || "Not generated yet"}
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (formData.webhook_secret) {
                        navigator.clipboard.writeText(formData.webhook_secret);
                        toast({ title: "Copied!", description: "Webhook secret copied to clipboard" });
                      }
                    }}
                    disabled={!formData.webhook_secret}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={async () => {
                  const secret = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '');
                  try {
                    const { error } = await supabase
                      .from('outlets')
                      .update({ webhook_secret: secret })
                      .eq('id', formData.id!);
                    if (error) throw error;
                    setFormData({ ...formData, webhook_secret: secret });
                    toast({ title: "✅ Webhook Secret Generated", description: "Copy and configure it in EpicPay." });
                  } catch (err: any) {
                    toast({ title: "Error", description: err.message, variant: "destructive" });
                  }
                }}
              >
                {formData.webhook_secret ? "🔄 Regenerate Secret" : "🔑 Generate Webhook Secret"}
              </Button>

              <div className="p-3 bg-muted rounded-md space-y-1">
                <p className="text-xs font-medium">Webhook Endpoint:</p>
                <code className="text-xs break-all select-all block p-2 bg-background rounded border">
                  {`https://uthqjaflckswjjgokjdw.supabase.co/functions/v1/epic-review-webhook`}
                </code>
              </div>
            </div>
          )}
          </div>

          {/* Scraping Configuration */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold">Scraping Configuration</h3>
            
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="scrape_enabled">Enable Scraping</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically scrape reviews for this outlet
                </p>
              </div>
              <Switch
                id="scrape_enabled"
                checked={formData.scrape_enabled ?? true}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, scrape_enabled: checked })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fetch_limit">Review Fetch Limit (Optional)</Label>
              <Input
                id="fetch_limit"
                type="number"
                min="10"
                max="1000"
                placeholder="Use global limit"
                value={formData.fetch_limit || ""}
                onChange={(e) =>
                  setFormData({ 
                    ...formData, 
                    fetch_limit: e.target.value ? parseInt(e.target.value) : undefined 
                  })
                }
              />
              <p className="text-sm text-muted-foreground">
                Override global fetch limit for this outlet (10-1000)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="google_scraper_version">Google Scraper Version (Optional)</Label>
              <Select
                value={formData.google_scraper_version || "google_places"}
                onValueChange={(value: "google_places" | "google_reviews_v2") =>
                  setFormData({ ...formData, google_scraper_version: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Use global setting" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google_places">Legacy (Places Crawler)</SelectItem>
                  <SelectItem value="google_reviews_v2">New (Reviews Scraper v2)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Override global Google scraper version for this outlet
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="review_source">Review Data Source</Label>
              <Select
                value={formData.review_source || "apify"}
                onValueChange={(value: "apify" | "oxylabs" | "dataforseo") =>
                  setFormData({ ...formData, review_source: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select data source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apify">Apify</SelectItem>
                  <SelectItem value="oxylabs">Oxylabs</SelectItem>
                  <SelectItem value="dataforseo">DataForSEO</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Choose which API to use for Google Maps review scraping
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={
                loading || 
                !formData.outlet_name ||
                (formData.review_source === 'dataforseo' && verificationStatus.verified !== true) ||
                (platformToggles.googleMaps && 
                 formData.google_maps_url && 
                 formData.review_source === 'apify' &&
                 validationStatus.valid !== true)
              }
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Outlet"
              )}
            </Button>
          </div>
          
          {platformToggles.googleMaps && formData.google_maps_url && formData.review_source === 'apify' && validationStatus.valid !== true && (
            <p className="text-sm text-destructive text-center">
              ⚠️ Please verify the Google Maps URL before saving (required for Apify)
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OutletModal;
